import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Paper, Tab, Tabs } from '@mui/material';
import Grid from 'material/components/Grid';

import {
  fetchActionAlert,
  fetchActionAlertLinks,
  fetchActionAlertRecipients,
  fetchActionCenter,
} from 'store/actions/campaigns';
import { campaignsSelectors } from 'store/selectors/campaigns';

import {
  Backdrop,
  BreadcrumbsNavigation,
  Container,
  LinksTable,
  RecipientsMap,
  RecipientsTable,
  TabPanel,
} from 'common/components';
import { colors } from 'utils/constants';

import { TabsWrapper } from 'globalStyles';

import ActionAlertOverview from '../components/ActionAlertOverview';
import ActionAlertTopCard from '../components/ActionAlertTopCard';

const StyledPaper = styled(Paper)`
  width: 100%;
`;

const StyledTabsWrapper = styled(TabsWrapper)`
  margin-bottom: 60px;
`;

const ActionAlertDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { actionAlertUuid, actionCenterUuid, campaignUuid, organizationUuid } = useParams();

  const {
    actionAlertDetails,
    actionAlertLinks,
    actionAlertRecipients,
    actionCenterDetails,
    isActionAlertFetching,
    isActionAlertLinksFetching,
    isActionAlertRecipientsFetching,
  } = useSelector(state => ({
    actionAlertDetails: campaignsSelectors.getActionAlertDetails(state),
    actionAlertLinks: campaignsSelectors.getActionAlertLinks(state),
    actionAlertRecipients: campaignsSelectors.getActionAlertRecipients(state),
    actionCenterDetails: campaignsSelectors.getActionCenterDetails(state),
    isActionAlertFetching: campaignsSelectors.isActionAlertFetching(state),
    isActionAlertLinksFetching: campaignsSelectors.isActionAlertLinksFetching(state),
    isActionAlertRecipientsFetching: campaignsSelectors.isActionAlertRecipientsFetching(state),
  }));

  useEffect(() => {
    if (
      Object.keys(actionCenterDetails).length === 0 ||
      actionCenterUuid !== actionCenterDetails.uuid
    ) {
      dispatch(fetchActionCenter(actionCenterUuid));
    }

    dispatch(fetchActionAlert(actionAlertUuid));
    dispatch(fetchActionAlertLinks(actionAlertUuid));
  }, [actionAlertUuid]);

  const [tabIndex, setTabIndex] = useState(0);
  const [tablesTabIndex, setTablesTabIndex] = useState(0);

  const { campaign, name, parent } = actionAlertDetails;

  const firstFetch = useRef({
    recipients: false,
  });

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/campaigns`,
      name: 'Advocacy',
    },
    {
      link: `/${organizationUuid}/campaigns/${campaignUuid}`,
      name: campaign?.name,
    },
    {
      link: `/${organizationUuid}/campaigns/${campaignUuid}/action-centers/${actionCenterUuid}?tab=0`,
      name: actionCenterDetails?.name,
    },
    {
      link: '',
      name,
    },
  ];

  const handleChangeTablesTab = newValue => {
    setTablesTabIndex(newValue);

    if (newValue === 1 && !firstFetch.current.recipients) {
      dispatch(fetchActionAlertRecipients(actionAlertUuid)).then(() => {
        firstFetch.current.recipients = true;
      });
    }
  };

  const handleOpenParentActionAlert = () => {
    setTablesTabIndex(0);
    history.push(
      `/${organizationUuid}/campaigns/${campaignUuid}/action-centers/${actionCenterUuid}/action-alerts/${parent}`
    );
    firstFetch.current.recipients = false;
  };

  const handleChangeTab = (_, newValue) => setTabIndex(newValue);

  return (
    <>
      <BreadcrumbsNavigation links={breadcrumbs} />
      <Container>
        <Grid container>
          <ActionAlertTopCard handleOpenParentActionAlert={handleOpenParentActionAlert} />
        </Grid>
        <Grid container>
          <ActionAlertOverview />
        </Grid>
        <TabsWrapper>
          <StyledPaper square variant="outlined">
            <Tabs
              indicatorColor="primary"
              onChange={handleChangeTab}
              value={tabIndex}
              slotProps={{
                indicator: {
                  style: {
                    backgroundColor: colors.secondary,
                  },
                },
              }}
              sx={{
                '& .MuiTab-root:not(.Mui-disabled):hover': {
                  color: 'primary.main',
                },
              }}
            >
              <Tab label="Contacts" />
              <Tab disabled label="Analytics" />
            </Tabs>
            <TabPanel index={0} value={tabIndex}>
              <RecipientsMap alertType="email" />
            </TabPanel>
          </StyledPaper>
        </TabsWrapper>
        <StyledTabsWrapper>
          <StyledPaper square variant="outlined">
            <Tabs
              indicatorColor="primary"
              onChange={(_, newValue) => handleChangeTablesTab(newValue)}
              value={tablesTabIndex}
              slotProps={{
                indicator: {
                  style: {
                    backgroundColor: colors.secondary,
                  },
                },
              }}
              sx={{
                '& .MuiTab-root:not(.Mui-disabled):hover': {
                  color: 'primary.main',
                },
              }}
            >
              <Tab label="Links" />
              <Tab label="Recipients" />
            </Tabs>
            <TabPanel index={0} value={tablesTabIndex}>
              <LinksTable links={actionAlertLinks} isDataFetching={isActionAlertLinksFetching} />
            </TabPanel>
            <TabPanel index={1} value={tablesTabIndex}>
              <RecipientsTable
                isDataFetching={isActionAlertRecipientsFetching}
                recipients={actionAlertRecipients}
              />
            </TabPanel>
          </StyledPaper>
        </StyledTabsWrapper>
        <Backdrop isOpen={isActionAlertFetching} />
      </Container>
    </>
  );
};

export default ActionAlertDetails;
